'use client'

import { useState } from 'react'
import { motion } from 'motion/react'
import Image from 'next/image'

import dashboardImage from '../assets/dashboardImage.png'
import Link from 'next/link'

export default function Hero() {
    const [activeTab, setActiveTab] = useState<'organizations' | 'players'>('organizations')

    return (
        <section className='relative min-h-screen overflow-hidden bg-[#0a0a0a] px-6 pt-32 pb-20 md:px-12 lg:px-24'>
            <div className='mx-auto max-w-7xl'>
                <div className='grid gap-12 lg:grid-cols-2 lg:gap-8'>
                    <div className='flex flex-col justify-center z-10'>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className='mb-8 flex w-fit rounded-full border border-zinc-800 bg-zinc-900/50 p-1 backdrop-blur-sm relative'
                        >
                            <button
                                onClick={() => setActiveTab('organizations')}
                                className={`relative z-10 rounded-full px-6 py-2 text-xs font-bold transition-colors ${
                                    activeTab === 'organizations' ? 'text-black' : 'text-zinc-400 hover:text-white'
                                }`}
                            >
                                FOR ORGANIZATIONS
                                {activeTab === 'organizations' && (
                                    <motion.div
                                        layoutId='activeTab'
                                        className='absolute inset-0 -z-10 rounded-full bg-[#b4ff00]'
                                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                    />
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('players')}
                                className={`relative z-10 rounded-full px-6 py-2 text-xs font-bold transition-colors ${
                                    activeTab === 'players' ? 'text-black' : 'text-zinc-400 hover:text-white'
                                }`}
                            >
                                FOR PLAYERS
                                {activeTab === 'players' && (
                                    <motion.div
                                        layoutId='activeTab'
                                        className='absolute inset-0 -z-10 rounded-full bg-[#b4ff00]'
                                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                    />
                                )}
                            </button>
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className='mb-6 text-5xl font-black leading-[1.1] tracking-tighter text-white md:text-7xl'
                        >
                            Run
                            <br />
                            <span className='text-[#b4ff00]'>
                                Your Own
                                <br />
                                EA FC Pro Clubs
                                <br />
                                Competition.
                            </span>
                            <br />
                            No developer.
                            <br />
                            No spreadsheets
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className='mb-10 max-w-md text-lg text-zinc-400'
                        >
                            Built for EA FC Pro Clubs organizers who are done managing leagues manually on, WhatsApp,
                            Discord and spreadsheets.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className='mb-12 flex flex-wrap gap-4'
                        >
                            <button className='rounded-sm bg-[#b4ff00] px-8 py-4 text-sm font-bold text-black transition-transform hover:scale-105 uppercase'>
                                START COMPETING
                            </button>
                            <button className='rounded-sm border border-zinc-700 bg-zinc-900/50 px-8 py-4 text-sm font-bold text-white transition-colors hover:bg-zinc-800 uppercase'>
                                WATCH DEMO
                            </button>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className='relative hidden lg:block'
                    >
                        <div className='absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-transparent to-transparent z-10' />
                        <div className='absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent z-10' />
                        <Image
                            src={dashboardImage}
                            alt='Disporty Dashboard'
                            fill
                            className='object-cover object-left opacity-80'
                            priority
                        />

                        {/* Center Call to Action */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                            className='absolute inset-0 z-30 flex flex-col items-center justify-center gap-4'
                        >
                            <div className='px-12 py-4'>
                                <span className='text-2xl font-black tracking-[0.2em] text-white'>COMPETELY FREE</span>
                            </div>
                            <Link
                                href='/signup'
                                className='flex items-center justify-center rounded-xl bg-white px-10 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.5)] transition-transform hover:scale-105'
                            >
                                <span className='text-lg font-bold text-black font-sans'>Sign up Now</span>
                            </Link>
                        </motion.div>

                        {/* Floating Status & USPs */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.8 }}
                            className='absolute bottom-20 right-1 z-20 flex flex-col items-end gap-10'
                        >
                            {/* Status Badge */}
                            <div className='rounded-l-lg border-y border-l border-zinc-800 bg-zinc-900/80 p-4 backdrop-blur-md shadow-2xl'>
                                <div className='mb-1 text-[10px] font-bold tracking-widest text-zinc-500 uppercase'>
                                    Live Feed Status
                                </div>
                                <div className='flex items-center gap-2 text-sm font-bold text-white'>
                                    <div className='h-2 w-2 rounded-full bg-[#b4ff00] animate-pulse' />
                                    UCL ENGINES ACTIVE
                                </div>
                            </div>

                            {/* USPs List */}
                            <div className='flex flex-wrap justify-end gap-4 mr-4 text-right'>
                                {[
                                    'connected with EA account',
                                    'own branding',
                                    'Fully automated',
                                    'discord integrated'
                                ].map(usp => (
                                    <div
                                        key={usp}
                                        className='flex flex-row-reverse items-center gap-3 text-[11px] font-black tracking-widest text-zinc-400 hover:text-[#b4ff00] transition-colors uppercase'
                                    >
                                        {usp}
                                        <div className='h-1.5 w-1.5 bg-[#b4ff00] shadow-[0_0_8px_#b4ff00]' />
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    )
}
